// Pure helpers for the "Tietolähteet" panel. The feed list itself comes from
// the Dataverse table clarito_feedsource via the read flow (`sources` in the
// /api/updates response, parsed in src/api.ts); nothing here is a static copy.

import type { FeedSource, ParsedItem } from './types';

export type SourceStat = { count: number; newest: Date | null };

export type SourceStats = {
  // Keyed by feed name; every name passed in has an entry (count 0 when absent).
  byFeed: Map<string, SourceStat>;
  // Rows whose feed name is not among the given names, e.g. a feed renamed or
  // removed in the table while its older rows are still in the window.
  other: SourceStat & { feeds: string[] };
};

// Per-feed item count and newest publishedDate, matching each row's
// sourceFeed (cr436_sourcefeedtext) to a feed name exactly. Pass the raw loaded
// data (not mute- or bookmark-filtered) so the numbers reflect ingestion health.
export function computeSourceStats(
  items: readonly ParsedItem[],
  feedNames: readonly string[],
): SourceStats {
  const byFeed = new Map<string, SourceStat>(
    feedNames.map((name) => [name, { count: 0, newest: null }]),
  );
  const other: SourceStats['other'] = { count: 0, newest: null, feeds: [] };

  for (const item of items) {
    const stat = byFeed.get(item.sourceFeed) ?? other;
    if (stat === other && !other.feeds.includes(item.sourceFeed)) {
      other.feeds.push(item.sourceFeed);
    }
    stat.count += 1;
    const d = new Date(item.publishedDate);
    if (!isNaN(d.getTime()) && (!stat.newest || d > stat.newest)) stat.newest = d;
  }

  return { byFeed, other };
}

// Distinct feed names present in the loaded rows, alphabetical (fi-FI). Used
// as the fallback listing when the backend returned no `sources`.
export function feedNamesFromItems(items: readonly ParsedItem[]): string[] {
  return Array.from(new Set(items.map((i) => i.sourceFeed).filter((f) => f))).sort(
    (a, b) => a.localeCompare(b, 'fi-FI'),
  );
}

// Sources ordered by sortOrder (nulls last); ties keep the upstream order.
export function sortSources(sources: readonly FeedSource[]): FeedSource[] {
  return sources
    .map((s, i) => ({ s, i }))
    .sort((a, b) => {
      const ao = a.s.sortOrder ?? Number.POSITIVE_INFINITY;
      const bo = b.s.sortOrder ?? Number.POSITIVE_INFINITY;
      return ao === bo ? a.i - b.i : ao - bo;
    })
    .map(({ s }) => s);
}

// The daily flow writes 'OK' on a successful fetch, otherwise the failure text.
export function isStatusOk(status: string | null): boolean {
  return status !== null && status.trim().toUpperCase() === 'OK';
}

// True when an enabled official source's last run reported anything other
// than OK. A source that has never run (lastStatus null) is not flagged, and
// community (blog) sources never raise the header warning; their status still
// shows in the panel.
export function hasSourceWarning(sources: readonly FeedSource[]): boolean {
  return sources.some(
    (s) =>
      s.sourceType !== 'community' &&
      s.enabled &&
      s.lastStatus !== null &&
      !isStatusOk(s.lastStatus),
  );
}

// Splits sources into the panel's "Viralliset" and "Yhteisö" sections,
// keeping their order. Only sourceType 'community' is community.
export function splitSourcesByType(sources: readonly FeedSource[]): {
  official: FeedSource[];
  community: FeedSource[];
} {
  return {
    official: sources.filter((s) => s.sourceType !== 'community'),
    community: sources.filter((s) => s.sourceType === 'community'),
  };
}

// A feed whose newest loaded item is older than this is "stale". An empty feed
// is not an error for the flow (lastStatus stays OK), so a feed whose URL went
// dead-but-valid would otherwise go unnoticed — as the M365 Copilot Blog did
// for a month from 4.9.2026.
export const STALE_AFTER_DAYS = 30;
// Length of the data window /api/updates serves (set in the read flow, not
// here); used only to word the "no items" note.
export const DATA_WINDOW_DAYS = 180;
const DAY_MS = 86_400_000;

// 'old': newest item older than STALE_AFTER_DAYS; 'empty': no items at all.
export type Staleness = 'old' | 'empty';

// Staleness of one source given its stats from the loaded data. Only enabled
// official sources that have been fetched at least once (lastRun set) can be
// stale, so a brand-new row is not flagged before its first run. Community
// blogs post irregularly, so they are never stale. Exactly STALE_AFTER_DAYS
// old is still fresh. `now` is injectable for tests.
export function sourceStaleness(
  source: FeedSource,
  stat: SourceStat | undefined,
  now: Date = new Date(),
): Staleness | null {
  if (source.sourceType === 'community' || !source.enabled || !source.lastRun) return null;
  if (!stat || stat.count === 0) return 'empty';
  // Items exist but none has a parseable date: freshness can't be shown, so
  // flag it as old rather than as having no items.
  if (!stat.newest) return 'old';
  return now.getTime() - stat.newest.getTime() > STALE_AFTER_DAYS * DAY_MS ? 'old' : null;
}

// True when any enabled, already-fetched source is stale in the loaded data.
// Pass the raw loaded data (not mute- or bookmark-filtered).
export function hasStaleSource(
  sources: readonly FeedSource[],
  items: readonly ParsedItem[],
  now: Date = new Date(),
): boolean {
  const { byFeed } = computeSourceStats(items, sources.map((s) => s.name));
  return sources.some((s) => sourceStaleness(s, byFeed.get(s.name), now) !== null);
}
